/* eslint-disable */
import React, { useEffect, useRef, useState } from "react";
import {
  Table, Button, Tag, Space, Tooltip, Row, Col, Input,
  Select, DatePicker, Spin, Empty, message, Avatar,
} from "antd";
import {
  PlusOutlined, ExportOutlined, SearchOutlined, FilterOutlined,
  ReloadOutlined, EyeOutlined, FileExcelOutlined, FilePdfOutlined,
  BuildOutlined, TeamOutlined, CheckCircleOutlined, CloseCircleOutlined,
  CopyOutlined, BankOutlined, ShopOutlined,
  PhoneOutlined,
  MailOutlined,
} from "@ant-design/icons";
import qs from "qs";
import { useNavigate } from "react-router-dom";
import Layout from "../../components/Layout";
import AntDropdown from "../../components/CustomDropdown/AntDropdown";
import noDataLogo from "../../assets/images/no-data.png";
import CreateSingleUserModal from "../../components/Modal/User/CreateSingleUser";
import AntTable from "../../components/CustomTable/AntTable";
import { customBaseUrl, httpDelete, httpGet, httpPost } from "../../store/http";
import CreateBulkUser from "../../components/Modal/User/CreateBulkUser";
import UserActionsModal from "../../components/Modal/User/UserActions";
import DeactivateUsers from "../../components/Modal/User/DeactivateUsers";
import { hideLoader, showLoader } from "../../utils/loader";
import { downloadFile } from "../../utils/fileDownloader";
import Swal from "sweetalert2";
import CreateBulkForm from "../../components/Modal/User/CreateBulkForm";
import { NotificationManager } from "react-notifications";
import EmailInvite from "./modals/emailInvite";
import SocialInvites from "./modals/socialInvites";
import ExportCSV from "./export";
import VerifyUser from "./verify";
import PinModal from "../../components/Modal/Pin/PinModal";
import moment from "moment";
import { CSVLink } from "react-csv";
import generatePDF from "./generatePdf";
import UserProfiles from "./profiles";
import PageHeadingBanner from "../../components/shared/PageHeadingBanner";
import "./ManageUser.css";

const { Option } = Select;
const { RangePicker } = DatePicker;

/* ──────────────────────────── Status Badge ───────────────────────── */
const StatusBadge = ({ status }) => {
  const isActive = (status || "").toUpperCase() === "ACTIVE";
  return isActive ? (
    <span className="mu-status-active">
      <span className="mu-status-dot" style={{ background: "#22c55e" }} />
      Active
    </span>
  ) : (
    <span className="mu-status-inactive">
      <span className="mu-status-dot" style={{ background: "#ef4444" }} />
      Inactive
    </span>
  );
};

/* ──────────────────────────── Company Avatar ──────────────────────── */
const CompanyAvatar = ({ name }) => {
  const letter = (name || "?")[0].toUpperCase();
  return (
    <div className="mu-company-avatar">{letter}</div>
  );
};

/* ──────────────────────────── Copy util ──────────────────────────── */
const copyText = (text) => {
  if (navigator.clipboard) {
    navigator.clipboard.writeText(text).then(() => message.success("Copied!"));
  }
};

/* ──────────────────────────── ResizeObserver Polyfill ─────────────── */
// Prevent 'ResizeObserver loop limit exceeded' error in dev environment
if (typeof window !== "undefined") {
  const _ResizeObserver = window.ResizeObserver;
  window.ResizeObserver = class ResizeObserver extends _ResizeObserver {
    constructor(callback) {
      super((entries, observer) => {
        window.requestAnimationFrame(() => {
          callback(entries, observer);
        });
      });
    }
  };
}

/* ──────────────────────────── Main Component ─────────────────────── */
const Index = () => {
  const navigate = useNavigate();
  const roles = localStorage.getItem("roles");

  /* ── State ── */
  const [users, setUsers] = useState([]);
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [showBulkSms, setShowBulkSms] = useState(false);
  const [showEmail, setShowEmail] = useState(false);
  const [showCreateUserExcel, setShowCreateUserExcel] = useState(false);
  const [showCreateUserForms, setShowCreateUserForms] = useState(false);
  const [showSocial, setShowSocial] = useState(false);
  const [showUserAction, setShowUserAction] = useState(false);
  const [showDeactivateUser, setShowDeactivateUser] = useState(false);
  // Removed showFilterBar state since it's permanently shown

  const [pagination, setPagination] = useState({ current: 0, pageSize: 50 });
  const [filteredDate, setFilteredDate] = useState([]);
  const [filteredData, setFilteredData] = useState({});
  const [selectedRecord, setRecord] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [totalRegisteredUsers, setTotalRegisteredUsers] = useState(0);
  const [activeMerchants, setActiveMerchants] = useState(0);
  const [inactiveMerchants, setInactiveMerchants] = useState(0);
  const [onboardedThisMonth, setOnboardedThisMonth] = useState(0);
  const [statsLoading, setStatsLoading] = useState(true);
  const [showExport, setShowExport] = useState(false);
  const [showProfiles, setShowProfile] = useState(false);
  const [showVerify, setShowVerify] = useState(false);
  const [showVerifyAccount, setShowVerifyAccount] = useState(false);
  const [otp, setOtp] = useState("");
  const [phone, setPhone] = useState("");
  const [reload, setReload] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  /* ── Initial Stats Fetching ── */
  useEffect(async () => {
    // Only fetch stats if needed, or leave it to rely on search results.
    // The user requested NO merchants until searched for.
    // We'll fetch the total count without populating the table.
    try {
      setStatsLoading(true);
      // Fetch a larger amount initially just to compute accurate stats across the board
      const res = await httpGet(`${customBaseUrl.integUrl}/api/v1/merchants?size=10000`);
      if (res?.status) {
        let merchants = typeof res.data === "string" ? JSON.parse(res.data) : res.data;
        const allMerchants = merchants?.content || [];
        setTotalRegisteredUsers(merchants?.totalElements || 0);

        // Compute active vs inactive
        const activeCount = allMerchants.filter(m => m.status === 'ACTIVE').length;
        const inactiveCount = allMerchants.filter(m => m.status !== 'ACTIVE').length;

        // Compute onboarded this month
        const currentMonth = moment().format('YYYY-MM');
        const onboardedMonthCount = allMerchants.filter(m => moment(m.createdAt).format('YYYY-MM') === currentMonth).length;

        setActiveMerchants(activeCount);
        setInactiveMerchants(inactiveCount);
        setOnboardedThisMonth(onboardedMonthCount);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setStatsLoading(false);
    }
  }, [reload]);

  /* ── Filter & search ── */
  const handleGetUsers = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    if (!filteredData?.email && !filteredData?.phoneNumber && !filteredData?.firstName && !filteredDate?.fromdate && !filteredDate?.todate) {
      NotificationManager.warning("Please enter at least one search criteria.");
      return;
    }

    setLoading(true);
    setHasSearched(true);
    showLoader();

    const query = {
      fromDate: filteredDate?.fromdate ? moment(filteredDate.fromdate).format("YYYY-MM-DD") : undefined,
      toDate: filteredDate?.todate ? moment(filteredDate.todate).format("YYYY-MM-DD") : undefined,
      email: filteredData?.email || undefined,
      phoneNumber: filteredData?.phoneNumber || undefined,
      companyName: filteredData?.firstName || undefined,
      // Fetch a large size to allow effective client-side filtering
      size: 10000,
      page: 0,
    };

    // Remove undefined values
    const cleanQuery = Object.fromEntries(Object.entries(query).filter(([_, v]) => v != null && v !== ""));

    const res = await httpGet(`${customBaseUrl.integUrl}/api/v1/merchants?${qs.stringify(cleanQuery)}`);

    setLoading(false);
    hideLoader();

    if (res?.status) {
      let merchants;
      if (typeof res.data === "string") {
        try {
          merchants = JSON.parse(res.data);
        } catch (e) {
          console.error("Failed to parse merchants data", e);
          NotificationManager.error("Failed to process search results.");
          setUsers([]);
          return;
        }
      } else {
        merchants = res.data;
      }

      let finalUsers = merchants?.content || [];

      // Apply client-side filters since the backend API currently ignores the search query parameters
      if (query.email) {
        finalUsers = finalUsers.filter(u => u.email?.toLowerCase().includes(query.email.toLowerCase()));
      }
      if (query.phoneNumber) {
        finalUsers = finalUsers.filter(u => u.phoneNumber?.includes(query.phoneNumber));
      }
      if (query.companyName) {
        finalUsers = finalUsers.filter(u => u.companyName?.toLowerCase().includes(query.companyName.toLowerCase()));
      }
      if (query.fromDate) {
        finalUsers = finalUsers.filter(u => moment(u.createdAt).isSameOrAfter(moment(query.fromDate).startOf('day')));
      }
      if (query.toDate) {
        finalUsers = finalUsers.filter(u => moment(u.createdAt).isSameOrBefore(moment(query.toDate).endOf('day')));
      }

      setUsers(finalUsers);
      setPagination({ ...pagination, total: finalUsers.length, current: 1, pageSize: finalUsers.length > 50 ? finalUsers.length : 50 });
    } else {
      NotificationManager.error(res?.message || "Search failed. No results found or an error occurred.");
      setUsers([]);
    }
  };

  /* ── Delete ── */
  const handleDeleteUser = async (id) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ff6700",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    }).then(async (result) => {
      if (result.value) {
        showLoader();
        const res = await httpDelete(`${customBaseUrl.authUrl}/api/v1/user/delete/${id}`);
        if (res.status === true) {
          hideLoader();
          NotificationManager.success("User Deleted Successfully");
          setReload(!reload);
        } else {
          hideLoader();
          Swal.fire({ icon: "error", title: "Oops...", text: res.message });
        }
      }
    });
  };

  /* ── Reset Creds ── */
  const handleResetCredentials = (user) => {
    Swal.fire({
      title: "Reset Credentials",
      text: "Send reset OTP to this user?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ff6700",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, reset!",
    }).then((result) => {
      if (result.value) {
        httpGet(`${customBaseUrl.authUrl}/api/v1/password/change-password/byEmail?email=${user.email}`);
        NotificationManager.success("Credentials Reset Successfully");
      }
    });
  };

  /* ── Lock Toggle ── */
  const handleRToggleLock = (userId, record) => {
    Swal.fire({
      title: "Are you sure?",
      text: `About to ${record?.accountNonLocked ? "Lock" : "Unlock"} this user!`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ff6700",
      cancelButtonColor: "#d33",
      confirmButtonText: record?.accountNonLocked ? "Yes, lock!" : "Yes, Unlock",
    }).then((result) => {
      if (result.value) {
        httpPost(`${customBaseUrl.authUrl}/api/v1/admin/users/lock-toggle/un-lock?userId=${userId}`, {});
        NotificationManager.success("Action completed");
      }
    });
  };

  /* ── Verify Account ── */
  const verify = async (email) => {
    showLoader();
    setPhone(email);
    const res = await httpGet(`${customBaseUrl.authUrl}/api/v1/auth/resend-otp/signup/${email}`);
    if (res.status) {
      hideLoader();
      Swal.fire("Done", res.message, "success").then(() => {
        setShowVerify(false);
        setShowVerifyAccount(true);
      });
    } else {
      hideLoader();
      Swal.fire("error", res?.message);
    }
  };

  const completeVerification = async () => {
    showLoader();
    const res = await httpPost(`${customBaseUrl.authUrl}/api/v1/auth/verify-otp`, {
      otp: Number(otp),
      phoneOrEmail: phone,
    });
    if (res.status) {
      hideLoader();
      setOtp(""); setPhone("");
      setShowVerifyAccount(false);
      Swal.fire("Done", res.message, "success");
    } else {
      hideLoader();
      setOtp("");
      Swal.fire("Oops!", res.message, "error");
    }
  };

  /* ── Deactivate ── */
  const handleDactivateUser = async (userId) => {
    showLoader();
    let d = new Date();
    const res = await httpPost(`${customBaseUrl.logUrl}/api/v1/requestlog/create`, {
      payload: JSON.stringify({ userId }),
      requestId: `REQ${d.getTime()}`,
      requestType: "ACTIVATIONTOGGLE",
      customerName: selectedRecord?.firstName + " " + selectedRecord?.surname,
    });
    hideLoader();
    if (res.status === true) {
      setShowDeactivateUser(false);
      Swal.fire(res.message, "success");
    } else {
      Swal.fire(res.message, "error");
    }
  };

  /* ── Dropdown action handler ── */
  const handleDropdownAction = (item, id, record) => {
    setSelectedId(id);
    setRecord(record);
    localStorage.setItem("selectedUser", JSON.stringify(record));

    if (item === "Manage Business Profile") navigate(`/users/profile/${record.merchantId}/page`);
    if (item === "Manage Transaction") setShowUserAction(true);
    if (item === "Transaction History") navigate(`/transactions_history/${id}`);
    if (item === "Manage Kyc") navigate(`/users_kyc/${id}`);
    if (item === "Deactivate User" || item === "Activate User") handleDactivateUser(id);
    if (item === "Manage Profile") navigate(`/users/profile/${id}`);
    if (item === "Login Tracker") navigate(`/user/login_tracker/${id}`);
    if (item === "Manage Team Members") navigate(`/users/manage_team/${id}`);
    if (item === "Verify") verify(record?.email ?? record?.phoneNumber);
    if (item === "Activity Log") navigate(`/user/activity_log/${id}`);
    if (item === "Delete User") handleDeleteUser(id);
    if (item === "Reset Credentials") handleResetCredentials(record);
    if (
      item === "Block User" || item === "Unblock User" ||
      item === "Unlock Transaction" || item === "Lock Transaction"
    ) handleRToggleLock(id, record);
  };

  /* ── Columns ── */
  const columns = [
    {
      title: "Business",
      dataIndex: "companyName",
      width: 220,
      render: (name, record) => (
        <div className="mu-company-name">
          <CompanyAvatar name={name} />
          <div>
            <div className="mu-company-text">{name || "—"}</div>
            <div className="mu-company-sub">{record.accountHolderName || ""}</div>
          </div>
        </div>
      ),
    },
    {
      title: "Phone",
      dataIndex: "phoneNumber",
      width: 140,
      render: (v) => <span style={{ fontSize: 13, color: "#595959" }}>{v || "—"}</span>,
    },
    {
      title: "Email",
      dataIndex: "email",
      width: 200,
      ellipsis: true,
      render: (v) => <span style={{ fontSize: 13, color: "#595959" }}>{v || "—"}</span>,
    },
    {
      title: "Client ID",
      dataIndex: "clientId",
      width: 160,
      render: (v) => v ? (
        <Tooltip title="Click to copy">
          <span className="mu-client-id" style={{ cursor: "pointer" }} onClick={() => copyText(v)}>
            {v}
          </span>
        </Tooltip>
      ) : "—",
    },
    {
      title: "Status",
      dataIndex: "status",
      width: 120,
      render: (s) => <StatusBadge status={s} />,
    },
    {
      title: "Date Created",
      dataIndex: "createdAt",
      width: 150,
      render: (d) => <span className="mu-date-cell">{d ? moment(d).format("DD MMM YYYY") : "—"}</span>,
      sorter: (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
      defaultSortOrder: "descend",
    },
    {
      title: "",
      dataIndex: "id",
      width: 80,
      render: (id, record) => (
        <AntDropdown
          dropDownOptions={["Manage Business Profile"]}
          handleDropdownAction={(item) =>
            handleDropdownAction(item, record?.merchantId, record)
          }
          userId={id}
          isArrow
        />
      ),
    },
  ];

  /* ── Clear filters ── */
  const handleClearFilters = () => {
    setFilteredData({});
    setFilteredDate([]);
    setUsers([]);
    setHasSearched(false);
  };

  return (
    <Layout>
      <div className="mu-wrapper">
        {/* ── Page Header ── */}
        <PageHeadingBanner
          icon={<ShopOutlined />}
          title="Manage Merchants"
          subtitle="View and manage all onboarded B2B merchants and businesses"
          actions={
            <>
              <Tooltip title="Refresh search results">
                <Button
                  className="phb-btn-glass"
                  icon={<ReloadOutlined />}
                  loading={loading}
                  onClick={handleGetUsers}
                >
                  Refresh
                </Button>
              </Tooltip>
              <Button
                className="phb-btn-solid"
                icon={<PlusOutlined />}
                onClick={() => setShowCreateUser(true)}
              >
                Onboard Business
              </Button>
            </>
          }
        />

        {/* ── Stats Row ── */}
        <Row gutter={[16, 16]} className="mu-stats-row">
          <Col xs={24} sm={12} md={8} lg={6}>
            <div className="mu-stat-card">
              <div className="mu-stat-icon-wrap" style={{ background: "#fff3eb" }}>
                <BuildOutlined style={{ color: "#ff6700", fontSize: 22 }} />
              </div>
              <div className="mu-stat-content">
                <span className="mu-stat-value">
                  {statsLoading ? <Spin size="small" /> : totalRegisteredUsers.toLocaleString()}
                </span>
                <span className="mu-stat-label">Total Businesses</span>
              </div>
            </div>
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <div className="mu-stat-card">
              <div className="mu-stat-icon-wrap" style={{ color: "#22c55e", background: "#f0fff4" }}>
                <CheckCircleOutlined style={{ fontSize: 22 }} />
              </div>
              <div className="mu-stat-content">
                <span className="mu-stat-value">
                  {statsLoading ? <Spin size="small" /> : activeMerchants}
                </span>
                <span className="mu-stat-label">Active</span>
              </div>
            </div>
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <div className="mu-stat-card">
              <div className="mu-stat-icon-wrap" style={{ color: "#ef4444", background: "#fef2f2" }}>
                <CloseCircleOutlined style={{ fontSize: 22 }} />
              </div>
              <div className="mu-stat-content">
                <span className="mu-stat-value">
                  {statsLoading ? <Spin size="small" /> : inactiveMerchants}
                </span>
                <span className="mu-stat-label">Inactive</span>
              </div>
            </div>
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <div className="mu-stat-card">
              <div className="mu-stat-icon-wrap" style={{ color: "#8b5cf6", background: "#f5f3ff" }}>
                <TeamOutlined style={{ fontSize: 22 }} />
              </div>
              <div className="mu-stat-content">
                <span className="mu-stat-value">
                  {statsLoading ? <Spin size="small" /> : onboardedThisMonth}
                </span>
                <span className="mu-stat-label">Onboarded This Month</span>
              </div>
            </div>
          </Col>
        </Row>

        {/* ── Filter Bar (Permanently Visible) ── */}
        <div className="mu-filter-bar" style={{ marginTop: '20px' }}>
          <div className="mu-filter-title">
            <FilterOutlined />
            Search & Filter Merchants
          </div>
          <div className="mu-filter-grid">
            <div className="mu-filter-item">
              <span className="mu-filter-label">Business Name</span>
              <Input
                className="mu-filter-input"
                placeholder="e.g. Acme Ltd"
                prefix={<ShopOutlined style={{ color: "#bfbfbf" }} />}
                value={filteredData?.firstName || ""}
                onChange={(e) => setFilteredData({ ...filteredData, firstName: e.target.value })}
                onPressEnter={handleGetUsers}
                allowClear
              />
            </div>
            <div className="mu-filter-item">
              <span className="mu-filter-label">Phone Number</span>
              <Input
                className="mu-filter-input"
                placeholder="e.g. 08012345678"
                prefix={<PhoneOutlined style={{ color: "#bfbfbf" }} />}
                value={filteredData?.phoneNumber || ""}
                onChange={(e) => setFilteredData({ ...filteredData, phoneNumber: e.target.value })}
                onPressEnter={handleGetUsers}
                allowClear
              />
            </div>
            <div className="mu-filter-item">
              <span className="mu-filter-label">Email Address</span>
              <Input
                className="mu-filter-input"
                placeholder="e.g. info@company.com"
                prefix={<MailOutlined style={{ color: "#bfbfbf" }} />}
                value={filteredData?.email || ""}
                onChange={(e) => setFilteredData({ ...filteredData, email: e.target.value })}
                onPressEnter={handleGetUsers}
                allowClear
              />
            </div>
            <div className="mu-filter-item">
              <span className="mu-filter-label">Date Range</span>
              <RangePicker
                className="mu-filter-input"
                style={{ width: "100%", height: 38 }}
                value={[
                  filteredDate?.fromdate ? moment(filteredDate.fromdate) : null,
                  filteredDate?.todate ? moment(filteredDate.todate) : null
                ]}
                onChange={(dates) => {
                  setFilteredDate({
                    fromdate: dates && dates[0] ? dates[0].format("YYYY-MM-DD") : "",
                    todate: dates && dates[1] ? dates[1].format("YYYY-MM-DD") : "",
                  });
                }}
                format="DD MMM YYYY"
              />
            </div>
            {/* Moved actions inside grid for inline layout */}
            <div className="mu-filter-actions">
              <Button className="mu-btn-search" onClick={handleGetUsers}>
                <SearchOutlined /> Search
              </Button>
              <Button className="mu-btn-clear" onClick={handleClearFilters}>
                Clear
              </Button>
            </div>
          </div>
        </div>
        {/* End Filter Bar */}

        {/* ── Table Card ── */}
        <div className="mu-table-card">
          <div className="mu-table-header-row">
            <span className="mu-table-count">
              Showing <strong>{users.length.toLocaleString()}</strong> merchant{users.length !== 1 ? "s" : ""}
              {totalRegisteredUsers > users.length && (
                <span style={{ color: "#8c8c8c" }}> of {totalRegisteredUsers.toLocaleString()} total</span>
              )}
            </span>
            <div className="mu-table-download-btns">
              <Tooltip title="Export to Excel">
                <Button
                  className="mu-download-btn mu-download-btn-excel"
                  icon={<FileExcelOutlined />}
                  onClick={() => setShowExport({ type: 'excel' })}
                >
                  Export as Excel
                </Button>
              </Tooltip>
              <Tooltip title="Export to PDF">
                <Button
                  className="mu-download-btn mu-download-btn-pdf"
                  icon={<FilePdfOutlined />}
                  onClick={() => setShowExport({ type: 'pdf' })}
                >
                  Export as PDF
                </Button>
              </Tooltip>
            </div>
          </div>

          {users.length > 0 ? (
            <Table
              className="mu-table"
              columns={columns}
              dataSource={users}
              rowKey={(r) => r.merchantId || r.clientId || r.id || Math.random().toString()}
              loading={loading}
              scroll={{ x: 900 }}
              rowClassName={(_, idx) => (idx % 2 === 0 ? "mu-row-even" : "mu-row-odd")}
              pagination={{
                current: pagination.current,
                pageSize: pagination.pageSize,
                total: pagination.total,
                showSizeChanger: true,
                pageSizeOptions: ["20", "50", "100"],
                showTotal: (total, range) => `${range[0]}–${range[1]} of ${total}`,
              }}
              locale={{
                emptyText: (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={loading ? "Searching…" : "Search for businesses to view results"}
                  />
                ),
              }}
            />
          ) : loading ? (
            <div style={{ textAlign: "center", padding: "60px 0" }}>
              <Spin size="large" />
            </div>
          ) : !hasSearched ? (
            <div className="mu-empty-state" style={{ marginTop: '20px' }}>
              <img src={noDataLogo} alt="Search" style={{ height: 180, opacity: 0.7 }} />
              <h4>Search for Merchants</h4>
              <p>Enter your search criteria above to find businesses</p>
            </div>
          ) : (
            <div className="mu-empty-state" style={{ marginTop: '20px' }}>
              <img src={noDataLogo} alt="No data" style={{ height: 180, opacity: 0.7 }} />
              <h4>No Merchants Found</h4>
              <p>No businesses matched your search criteria.</p>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                style={{
                  marginTop: 20, background: "#ff6700", borderColor: "#ff6700",
                  borderRadius: 10, fontWeight: 600, height: 40, padding: "0 24px",
                }}
                onClick={() => setShowCreateUser(true)}
              >
                Onboard First Business
              </Button>
            </div>
          )}
        </div>

        {/* ── Modals ── */}
        {showDeactivateUser && (
          <DeactivateUsers
            showModal={showDeactivateUser}
            hideModal={setShowDeactivateUser}
            title="Deactivate Single User"
            handleDactivateUser={handleDactivateUser}
            users={users}
          />
        )}
        {showCreateUser && (
          <CreateSingleUserModal
            showModal={showCreateUser}
            hideModal={setShowCreateUser}
            title="Onboard New Business"
            reload={reload}
            setReload={setReload}
          />
        )}
        {showCreateUserExcel && (
          <CreateBulkUser
            showModal={showCreateUserExcel}
            hideModal={setShowCreateUserExcel}
            title="Upload Excel Sheet to Create Users"
          />
        )}
        {showCreateUserForms && (
          <CreateBulkForm
            showModal={showCreateUserForms}
            hideModal={setShowCreateUserForms}
            title="Create Single or Bulk Users Online"
          />
        )}
        {showUserAction && (
          <UserActionsModal
            showModal={showUserAction}
            hideModal={setShowUserAction}
            userId={selectedId}
            record={selectedRecord}
          />
        )}
        {showEmail && (
          <EmailInvite
            showModal={showEmail}
            hideModal={setShowEmail}
            type="email"
          />
        )}
        {showBulkSms && (
          <EmailInvite
            showModal={showBulkSms}
            hideModal={setShowBulkSms}
            type="sms"
          />
        )}
        {showSocial && (
          <SocialInvites
            showModal={showSocial}
            hideModal={setShowSocial}
          />
        )}
        {showExport && (
          <ExportCSV
            showModal={showExport}
            hideModal={setShowExport}
            dataList={users}
            onExportPdf={(filteredUsers, dates) => {
              generatePDF(filteredUsers, dates);
            }}
          />
        )}
        {showVerify && (
          <VerifyUser
            showModal={showVerify}
            hideModal={setShowVerify}
            title="Verify User"
            description="Enter verification details"
            buttonLabel="Verify"
            handleSubmit={() => { }}
          />
        )}
        {showVerifyAccount && (
          <PinModal
            center
            showModal={showVerifyAccount}
            hideModal={setShowVerifyAccount}
            otp={otp}
            setOtp={setOtp}
            separator=""
            title="Verify Account"
            description={`Please input the OTP sent to ${phone}`}
            buttonLabel="Verify"
            handleSubmit={completeVerification}
            loading={loading}
            numInputs={6}
          />
        )}
      </div>
    </Layout>
  );
};

export default Index;
